// Actuals → Work states (PRD TX1, P2d): counted hours by the state each person worked in, for payroll's
// state withholding. A travel record moves a trip's days to the state travelled to.

import { useState } from "react";
import { Badge, Caption1, Card, Field, Subtitle2 } from "@fluentui/react-components";
import { today } from "../../../core";
import { useApi } from "../api";
import { DateInput } from "../DateInput";
import { ErrorBar, Kpi, Loading } from "../ui";

interface StatesResponse {
  rows: { personId: string; state: string; hours: number; days: number; travel: boolean }[];
  totals: Record<string, number>;
}

const h = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 1 });
const monthStart = (d: string) => `${d.slice(0, 7)}-01`;

export function WorkStates({ names }: { names: Map<string, string> }) {
  const [from, setFrom] = useState(monthStart(today()));
  const [to, setTo] = useState(today());
  const data = useApi<StatesResponse>(from && to ? `/actuals/states?from=${from}&to=${to}` : null);
  const d = data.data;
  return (
    <Card className="panel wide">
      <Subtitle2>Hours by work state</Subtitle2>
      <Caption1 className="block muted">
        Each counted hour goes to the state the person worked in that day: a travel record on their person card when one covers the day, otherwise
        their work state. Hours only.
      </Caption1>
      <div className="toolbar">
        <Field label="From">
          <DateInput type="date" value={from} onChange={(_, x) => x.value && setFrom(x.value)} />
        </Field>
        <Field label="To">
          <DateInput type="date" value={to} onChange={(_, x) => x.value && setTo(x.value)} />
        </Field>
      </div>
      <ErrorBar message={data.error} />
      {!d ? (
        data.loading && <Loading />
      ) : !d.rows.length ? (
        <Caption1>No counted hours in these dates.</Caption1>
      ) : (
        <>
          <div className="kpis" aria-label="Hours by state">
            {Object.entries(d.totals)
              .sort((a, b) => b[1] - a[1])
              .map(([state, hours]) => (
                <Kpi key={state} label={state === "unknown" ? "No work state" : state} value={`${h(hours)} h`} />
              ))}
          </div>
          <table className="edit-table compact">
            <thead>
              <tr>
                <th>Person</th>
                <th>State</th>
                <th className="num">Days</th>
                <th className="num">Hours</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={`${r.personId}|${r.state}`}>
                  <td>{names.get(r.personId) ?? r.personId}</td>
                  <td>
                    {r.state === "unknown" ? "No work state" : r.state} {r.travel && <Badge appearance="tint">travel</Badge>}
                  </td>
                  <td className="num">{r.days}</td>
                  <td className="num">{h(r.hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}
